<template>
  <main class="deletion-page">
    <section class="hero">
      <div class="container hero-content">
        <p class="eyebrow">Yellow Bird Relationships · 黄鸟人际</p>
        <h1>账号与数据删除 / Account and Data Deletion</h1>
        <p class="lead">
          本页面由郑州云轩网络科技有限公司提供。即使您已经卸载应用，也可以在这里申请删除黄鸟人际账号及关联数据。
        </p>
        <p class="lead">
          This page is provided by Zhengzhou Yunxuan Network Technology Co., Ltd.
          You can request deletion of your Yellow Bird Relationships account and
          associated data even if the app is no longer installed.
        </p>
        <a class="primary-action" :href="deletionMailto">
          申请删除账号和数据 / Request deletion
        </a>
      </div>
    </section>

    <section class="content-section">
      <div class="container content-grid">
        <article class="content-card">
          <h2>申请方式</h2>
          <ol>
            <li>
              点击上方按钮，或发送邮件至
              <a href="mailto:legal@mail.yunxuanwl.cn">legal@mail.yunxuanwl.cn</a>。
            </li>
            <li>注明注册邮箱、希望删除的范围，以及可接收处理结果的联系方式。</li>
            <li>不要发送密码、验证码、身份证件照片或任何密钥。</li>
          </ol>
          <p>
            App 内入口：<strong>我的 → 隐私与账号 → 注销账号</strong>。
          </p>
        </article>

        <article class="content-card" lang="en">
          <h2>How to request deletion</h2>
          <ol>
            <li>
              Use the button above or email
              <a href="mailto:legal@mail.yunxuanwl.cn">legal@mail.yunxuanwl.cn</a>.
            </li>
            <li>
              Identify the email address used for the account, the deletion scope,
              and a contact channel for the result.
            </li>
            <li>Do not send a password, verification code, identity image, or secret key.</li>
          </ol>
          <p>
            In-app path: <strong>Me → Privacy &amp; Account → Delete account</strong>.
          </p>
        </article>

        <article class="content-card">
          <h2>删除范围与处理</h2>
          <p>
            身份核验完成并处理未完成业务后，我们将删除或匿名化账号资料、人物与关系记录，以及其他与账号关联且不再需要的数据。提交申请不代表删除已经完成，处理结果将回复至核验后的联系渠道。
          </p>
          <p>
            依法必须保留的交易、安全审计或争议记录，将在法定期限内隔离保存并限制用途。删除申请处理记录和必要备份的最长保留期为 6 个月，期满后删除或匿名化。
          </p>
        </article>

        <article class="content-card" lang="en">
          <h2>Scope and processing</h2>
          <p>
            After identity verification and resolution of outstanding activity, we
            delete or anonymize account profile data, person and relationship records,
            and other associated data that is no longer required. Submitting a request
            does not mean deletion is already complete; the result is sent to the
            verified contact channel.
          </p>
          <p>
            Transaction, security-audit, or dispute records that must be retained by law
            are isolated, access-restricted, and kept only for the required period.
            Deletion-request records and necessary backups are retained for no longer
            than six months and are then deleted or anonymized.
          </p>
        </article>
      </div>

      <div class="container notice-card">
        <h2>订阅说明 / Subscription notice</h2>
        <p>
          删除黄鸟人际账号不会自动取消由 Google Play 管理的订阅。如已订阅，请先在 Google Play 的“付款和订阅”中取消续订。
        </p>
        <p lang="en">
          Deleting a Yellow Bird Relationships account does not automatically cancel a
          subscription managed by Google Play. Cancel renewal in Google Play Payments
          &amp; subscriptions before requesting account deletion.
        </p>
      </div>
    </section>
  </main>
</template>

<script setup lang="ts">
const deletionMailto =
  "mailto:legal@mail.yunxuanwl.cn?subject=Yellow%20Bird%20Relationships%20account%20deletion%20request";
</script>

<style scoped lang="scss">
.deletion-page {
  padding-top: 80px;
  background: #f6f8fc;
  color: #25304a;
}

.hero {
  padding: 64px 0;
  color: #fff;
  background:
    radial-gradient(circle at 85% 10%, rgba(255, 211, 77, 0.34), transparent 28%),
    linear-gradient(135deg, #3157c8 0%, #604fdf 58%, #8a5ce6 100%);
}

.hero-content {
  max-width: 920px;
}

.eyebrow {
  margin-bottom: 14px;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #ffe69a;
}

h1 {
  max-width: 820px;
  margin-bottom: 24px;
  font-size: clamp(32px, 5vw, 52px);
  line-height: 1.18;
}

.lead {
  max-width: 820px;
  margin-bottom: 12px;
  font-size: 17px;
  line-height: 1.8;
  color: rgba(255, 255, 255, 0.92);
}

.primary-action {
  display: inline-flex;
  margin-top: 22px;
  padding: 14px 22px;
  border-radius: 12px;
  background: #fff;
  color: #3157c8;
  font-weight: 700;
  text-decoration: none;
  box-shadow: 0 10px 26px rgba(20, 25, 70, 0.22);
}

.content-section {
  padding: 48px 0 80px;
}

.content-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 24px;
}

.content-card,
.notice-card {
  border: 1px solid #e6eaf3;
  border-radius: 16px;
  background: #fff;
  box-shadow: 0 8px 24px rgba(31, 45, 90, 0.06);
}

.content-card {
  padding: 30px;
}

.notice-card {
  margin-top: 24px;
  padding: 28px 30px;
  border-left: 5px solid #f3b52f;
}

h2 {
  margin-bottom: 16px;
  font-size: 23px;
  color: #263a7a;
}

p,
li {
  font-size: 16px;
  line-height: 1.85;
  color: #4b5875;
}

p + p {
  margin-top: 12px;
}

ol {
  padding-left: 22px;
  margin-bottom: 14px;
}

li + li {
  margin-top: 8px;
}

a {
  color: #3157c8;
  overflow-wrap: anywhere;
}

@media (max-width: 768px) {
  .hero {
    padding: 46px 0;
  }

  .content-grid {
    grid-template-columns: 1fr;
  }

  .content-card,
  .notice-card {
    padding: 24px 20px;
  }
}
</style>
